import React from "react";
import PropTypes from 'prop-types';
import {
	useReactTable,
	getCoreRowModel,
	getSortedRowModel,
	getFilteredRowModel,
	flexRender,
} from "@tanstack/react-table";

function fuzzyTextFilterFn(row, columnId, filterValue) {
	if (!filterValue) return true;
	const rowValue = String(row.getValue(columnId) || '').toLowerCase();
	const filterString = String(filterValue).toLowerCase();
	return rowValue.includes(filterString);
}

const Table = React.forwardRef(({ columns, data, hiddenColumns = [] }, ref) => {
	const [globalFilter, setGlobalFilter] = React.useState('');

	const columnVisibility = React.useMemo(() => {
		const hidden = {};
		hiddenColumns.forEach(columnId => {
			if (columnId) hidden[columnId] = false;
		});
		return hidden;
	}, [hiddenColumns]);

	const table = useReactTable({
		data,
		columns,
		state: { 
			columnVisibility,
			globalFilter,
		},
		onGlobalFilterChange: setGlobalFilter,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		globalFilterFn: fuzzyTextFilterFn,
		enableColumnResizing: true,
		columnResizeMode: 'onChange',
	});

	React.useImperativeHandle(ref, () => ({
		setGlobalFilter: (value) => setGlobalFilter(value || ''),
		getGlobalFilter: () => globalFilter,
		getTable: () => table,
	}), [globalFilter, table]);

	const gridTemplate = table.getVisibleLeafColumns()
		.map(col => `${col.columnDef.size || 150}px`)
		.join(" ");

	return (
		<div className="table" style={{ width: "100%", overflow: "hidden" }}>
			{/* Header */}
			<div
				style={{
					position: "sticky",
					top: 0,
					zIndex: 1,
					background: "white",
					borderBottom: "1px solid #e5e7eb",
				}}
			>
				{table.getHeaderGroups().map((headerGroup) => (
					<div
						key={headerGroup.id}
						className="tr"
						style={{
							display: "grid",
							gridTemplateColumns: gridTemplate,
						}}
					>
						{headerGroup.headers.map((header) => {
							const sorted = header.column.getIsSorted();
							const canSort = header.column.getCanSort();
							
							return (
								<div
									key={header.id}
									className="th"
									onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
									style={{
										userSelect: "none",
										cursor: canSort ? "pointer" : "default",
										fontWeight: 600,
										padding: "8px 12px",
										borderRight: "1px solid #f3f4f6",
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{flexRender(header.column.columnDef.header, header.getContext())}
									{canSort && (
										<span style={{ marginLeft: 6 }}>
											{sorted === "asc" ? "⬆️" : sorted === "desc" ? "⬇️" : ""}
										</span>
									)}
								</div>
							);
						})}
					</div>
				))}
			</div>

			{/* Body */}
			<div style={{ maxHeight: "400px", overflow: "auto" }}>
				{table.getRowModel().rows.length === 0 ? (
					<div style={{ padding: '20px', textAlign: 'center' }}>
						No data available
					</div>
				) : (
					table.getRowModel().rows.map((row) => (
						<div
							key={row.id}
							className="tr"
							style={{ display: "grid", gridTemplateColumns: gridTemplate }}
						>
							{row.getVisibleCells().map((cell) => (
								<div
									key={cell.id}
									className="td"
									style={{ 
										overflow: "hidden", 
										textOverflow: "ellipsis", 
										whiteSpace: "nowrap",
										padding: "8px 12px",
										borderRight: "1px solid #f3f4f6",
									}}
								>
									{flexRender(cell.column.columnDef.cell, cell.getContext())}
								</div>
							))}
						</div>
					))
				)}
			</div>
		</div>
	);
});

Table.propTypes = {
	columns: PropTypes.arrayOf(PropTypes.object).isRequired,
	data: PropTypes.array.isRequired,
	hiddenColumns: PropTypes.arrayOf(PropTypes.string),
};

Table.displayName = 'Table';

export default Table;